import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Save, X, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { createChargeback, getCustomers, getContracts, getProducts } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const emptyLine = () => ({ productId: '', quantity: '', salesPrice: '' });

const NewChargeback = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    claimNumber: '',
    claimDate: '',
    customerId: '',
    contractId: '',
    totalAmount: '',
    status: 'IMPORTED',
    errorReason: ''
  });

  const [lineItems, setLineItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch customers, contracts and products for dropdowns
  const { data: customersData } = useQuery({ queryKey: ['customers'], queryFn: getCustomers, enabled: !!user });
  const { data: contractsData } = useQuery({ queryKey: ['contracts'], queryFn: getContracts, enabled: !!user });
  const { data: productsData } = useQuery({
    queryKey: ['products-for-lines'],
    queryFn: () => getProducts({ limit: 1000 }),
    enabled: !!user
  });

  const customers = customersData?.customers || [];
  const contracts = contractsData?.contracts || [];
  const products = Array.isArray(productsData)
    ? productsData
    : (productsData?.products || productsData?.data || []);

  const hasLines = lineItems.length > 0;
  const lineTotal = (li) => (Number(li.quantity) || 0) * (Number(li.salesPrice) || 0);
  const linesTotal = lineItems.reduce((sum, li) => sum + lineTotal(li), 0);
  const effectiveTotal = hasLines ? linesTotal : (parseFloat(formData.totalAmount) || 0);

  const createMutation = useMutation({
    mutationFn: createChargeback,
    onSuccess: (newChargeback) => {
      toast.success('Chargeback created successfully');
      queryClient.invalidateQueries({ queryKey: ['chargebacks'] });
      navigate(`/app/chargebacks/${newChargeback.id}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error creating chargeback');
      setIsSubmitting(false);
    }
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const addLine = () => setLineItems(prev => [...prev, emptyLine()]);
  const removeLine = (idx) => setLineItems(prev => prev.filter((_, i) => i !== idx));
  const updateLine = (idx, field, value) =>
    setLineItems(prev => prev.map((li, i) => (i === idx ? { ...li, [field]: value } : li)));

  const validateForm = () => {
    const newErrors = {};
    if (!formData.claimNumber.trim()) newErrors.claimNumber = 'Claim number is required';
    if (!formData.claimDate) newErrors.claimDate = 'Claim date is required';
    if (!formData.customerId) newErrors.customerId = 'Customer is required';

    if (hasLines) {
      const bad = lineItems.some(
        li => !li.productId || !(Number(li.quantity) > 0) || li.salesPrice === '' || Number(li.salesPrice) < 0
      );
      if (bad) newErrors.lineItems = 'Each line needs a product, a quantity greater than 0, and a sales price.';
    } else if (!formData.totalAmount || parseFloat(formData.totalAmount) <= 0) {
      newErrors.totalAmount = 'Enter a total amount, or add line items above.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        claimDate: new Date(formData.claimDate),
        totalAmount: effectiveTotal,
        contractId: formData.contractId || null,
        errorReason: formData.errorReason || null,
        lineItems: hasLines
          ? lineItems.map((li) => {
              const p = products.find(x => x.id === li.productId);
              return {
                productId: li.productId || null,
                quantity: Number(li.quantity) || 0,
                salesPrice: Number(li.salesPrice) || 0,
                productName: p?.name || null,
                productNdc: p?.ndc11 || p?.productNdc || null
              };
            })
          : undefined
      };
      await createMutation.mutateAsync(submitData);
    } catch (error) {
      console.error('Submit error:', error);
      setIsSubmitting(false);
    }
  };

  const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/app/chargebacks')}
            title="Back to chargebacks"
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create New Chargeback</h1>
            <p className="text-gray-600">Add a new chargeback claim to the system</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Chargeback Information</h2>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Claim Number *</label>
              <input
                type="text" name="claimNumber" value={formData.claimNumber} onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.claimNumber ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="Enter claim number" required
              />
              {errors.claimNumber && <p className="mt-1 text-sm text-red-600">{errors.claimNumber}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Claim Date *</label>
              <input
                type="date" name="claimDate" value={formData.claimDate} onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.claimDate ? 'border-red-300' : 'border-gray-300'}`}
                required
              />
              {errors.claimDate && <p className="mt-1 text-sm text-red-600">{errors.claimDate}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Customer *</label>
              <select
                name="customerId" value={formData.customerId} onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.customerId ? 'border-red-300' : 'border-gray-300'}`}
                required
              >
                <option value="">Select a customer</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>{customer.name} ({customer.type})</option>
                ))}
              </select>
              {errors.customerId && <p className="mt-1 text-sm text-red-600">{errors.customerId}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contract (Optional)</label>
              <select
                name="contractId" value={formData.contractId} onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select a contract (optional)</option>
                {contracts.map(contract => (
                  <option key={contract.id} value={contract.id}>{contract.number} - {contract.description}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Total Amount *</label>
              <input
                type="number" name="totalAmount"
                value={hasLines ? linesTotal.toFixed(2) : formData.totalAmount}
                onChange={handleInputChange}
                step="0.01" min="0" readOnly={hasLines}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.totalAmount ? 'border-red-300' : 'border-gray-300'} ${hasLines ? 'bg-gray-100 text-gray-700' : ''}`}
                placeholder="0.00" required={!hasLines}
              />
              {hasLines
                ? <p className="mt-1 text-xs text-gray-500">Calculated from line items.</p>
                : (errors.totalAmount && <p className="mt-1 text-sm text-red-600">{errors.totalAmount}</p>)}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                name="status" value={formData.status} onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="IMPORTED">Imported</option>
                <option value="VALIDATED">Validated</option>
                <option value="IN_ERROR">In Error</option>
                <option value="REJECTED">Rejected</option>
                <option value="APPROVED">Approved</option>
                <option value="POSTED">Posted</option>
              </select>
            </div>
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Line Items</label>
              <button
                type="button" onClick={addLine} title="Add line item"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100"
              >
                <Plus size={14} /> Add Line
              </button>
            </div>

            {hasLines ? (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Product (NDC)</th>
                      <th className="px-3 py-2 text-right font-medium w-24">Quantity</th>
                      <th className="px-3 py-2 text-right font-medium w-32">Sales Price</th>
                      <th className="px-3 py-2 text-right font-medium w-32">Line Total</th>
                      <th className="px-3 py-2 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {lineItems.map((li, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2">
                          <select
                            value={li.productId}
                            onChange={(e) => updateLine(idx, 'productId', e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md"
                          >
                            <option value="">Select a product…</option>
                            {products.map(p => (
                              <option key={p.id} value={p.id}>
                                {p.name} — NDC {p.ndc11 || p.productNdc || 'N/A'}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number" min="0" step="1" value={li.quantity}
                            onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-right"
                            placeholder="0"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number" min="0" step="0.01" value={li.salesPrice}
                            onChange={(e) => updateLine(idx, 'salesPrice', e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-right"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-900">{money(lineTotal(li))}</td>
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button" onClick={() => removeLine(idx)} title="Remove line"
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-3 py-2 text-right font-medium text-gray-600" colSpan={3}>Total</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-gray-900">{money(linesTotal)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg px-3 py-4 text-center">
                No line items. Add lines to build the claim (each matched to a product NDC), or enter a Total Amount above for a header-only claim.
              </p>
            )}
            {errors.lineItems && <p className="mt-1 text-sm text-red-600">{errors.lineItems}</p>}
          </div>

          {/* Error Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Error Reason (Optional)</label>
            <textarea
              name="errorReason" value={formData.errorReason} onChange={handleInputChange} rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter error reason if applicable..."
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button" onClick={() => navigate('/app/chargebacks')} title="Cancel"
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:border-transparent flex items-center gap-2"
          >
            <X size={16} /> Cancel
          </button>
          <button
            type="submit" disabled={isSubmitting} title="Create chargeback"
            className="px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Creating...</>
            ) : (
              <><Save size={16} /> Create Chargeback</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewChargeback;
